import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { usePageT } from '../i18n/createT';
import { MISC_T } from '../i18n/miscT';
import {
  CibFailurePanel,
  CibSuccessPanel,
  CibVerifyingPanel,
  buildInvoiceMetaFromCartOrder,
} from './CibPaymentResult';
import {
  buildCheckoutSearchParams,
  buildCibSuccessInfos,
  checkServicePayment,
  clearCheckoutContext,
  clearPendingCartId,
  clearSatimOrderId,
  CHECKOUT_PRICING_URL,
  fetchCartPaymentOrder,
  getCheckoutContext,
  getPendingCartId,
  getSatimOrderIdFromSearch,
  isPaymentSuccessful,
  paymentResultMessage,
  pickPaymentDetails,
  stripSatimParamsFromSearch,
} from '../services/checkoutApi';

const AFFILIATE_FLOW_KEY = 'kio_affiliate_flow_code';
const PRICING_TARGET = CHECKOUT_PRICING_URL;

export default function PaymentReturnHandler() {
  const location = useLocation();
  // Pages that verify their own SATIM return (AI credits: packager card payment, not a services cart).
  const ownsReturn = location.pathname === '/checkout' || location.pathname === '/ai-credits';
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = usePageT(MISC_T, lang);
  const { user, refreshUser } = useAuth();
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (ownsReturn) return undefined;

    const orderId = getSatimOrderIdFromSearch(location.search);
    if (!orderId) return undefined;

    const checkoutContext = getCheckoutContext();
    if (checkoutContext) {
      const params = buildCheckoutSearchParams(checkoutContext);
      const currentParams = new URLSearchParams(location.search);
      ['orderId', 'order_id', 'mdOrder', 'OrderId'].forEach((key) => {
        const value = currentParams.get(key);
        if (value) params.set(key, value);
      });
      navigate(`/checkout?${params.toString()}`, { replace: true });
      return undefined;
    }

    const cartId = getPendingCartId();
    if (!cartId) {
      setResult({
        orderId,
        error: t.checkout_payment_verify_error,
      });
      const nextSearch = stripSatimParamsFromSearch(location.search);
      navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
      return undefined;
    }

    let cancelled = false;
    setChecking(true);

    checkServicePayment(cartId, orderId)
      .then(async (data) => {
        if (!cancelled) {
          const success = isPaymentSuccessful(data);
          const details = pickPaymentDetails(data);
          let cartOrder = null;
          if (success) {
            try {
              cartOrder = await fetchCartPaymentOrder(cartId);
            } catch {
              cartOrder = null;
            }
          }
          setResult({
            orderId,
            success,
            data: success ? data : null,
            error: success ? null : paymentResultMessage(data, t),
            successInfos: success ? buildCibSuccessInfos(details, orderId, t) : [],
            cartOrder,
          });
          if (success) {
            refreshUser().catch(() => {});
            sessionStorage.removeItem(AFFILIATE_FLOW_KEY);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setResult({
            orderId,
            success: false,
            error: err.message || t.checkout_payment_verify_error,
          });
        }
      })
      .finally(() => {
        if (cancelled) return;
        clearPendingCartId();
        clearSatimOrderId();
        clearCheckoutContext();
        setChecking(false);
        const nextSearch = stripSatimParamsFromSearch(location.search);
        navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
      });

    return () => { cancelled = true; };
  }, [ownsReturn, location.pathname, location.search, navigate, t.checkout_payment_verify_error, refreshUser]);

  if (ownsReturn) return null;
  if (!checking && !result) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(17,17,17,0.45)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        width: '100%', maxWidth: 520, background: '#fff',
        borderRadius: 14, border: '1px solid #e3e3e3',
        padding: '24px 22px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
      }}>
        {checking ? (
          <CibVerifyingPanel t={t} />
        ) : result?.success ? (
          <CibSuccessPanel
            t={t}
            result={result.data}
            userEmail={user?.email}
            orderId={result.orderId || ''}
            cartOrder={result.cartOrder}
            successInfos={result.successInfos}
            invoiceMeta={buildInvoiceMetaFromCartOrder(user, result.cartOrder)}
            onContinue={() => {
              setResult(null);
              navigate(PRICING_TARGET, { replace: true });
            }}
          />
        ) : (
          <CibFailurePanel
            t={t}
            message={result?.error}
            onBackPricing={() => {
              setResult(null);
              navigate(PRICING_TARGET);
            }}
            onRetry={() => setResult(null)}
          />
        )}
      </div>
    </div>
  );
}
